// Pull the ClickFunnels purchase id off the order-confirmation page.
// Webhook ships it as `purchase.id` (numeric) — same value must be readable
// browser-side so the canonical event_id matches on both ends.

export interface CFExtraction {
  vendor_event_id: string;
  value?: number;
  currency?: string;
  order_id: string;
}

export function extractClickFunnels(): CFExtraction | null {
  const orderId = readFromUrl() ?? readFromGlobals() ?? readFromDom();
  if (!orderId) return null;

  const value = readValue();
  const currency = readCurrency();

  return {
    vendor_event_id: orderId,
    order_id: orderId,
    ...(value !== undefined ? { value } : {}),
    ...(currency ? { currency } : {}),
  };
}

function readFromUrl(): string | null {
  const url = new URL(window.location.href);
  // Direct purchase id keys, when CF actually exposes them.
  for (const k of ['purchase_id', 'cf_purchase_id', 'order_id', 'cf_order_id', 'cfo']) {
    const v = url.searchParams.get(k);
    if (v) return v;
  }
  const m = url.pathname.match(/\/(?:order-confirmation|order|thank-you|purchase)\/(\d+|[\w-]+)/i);
  if (m) return m[1]!;

  // Fallback: CF's order-complete pages don't expose purchase.id in the
  // DOM/URL — the page is rendered without order data. cf_uvid IS in the
  // URL and is stable per session, so we use it as the synthetic vendor id.
  // Server-side, we use the pending_events email_hash+window fallback to
  // match the eventual webhook (which arrives with the real purchase.id).
  // Prefix distinguishes synthetic ids from real purchase ids in logs.
  const cfUvid = url.searchParams.get('cf_uvid');
  if (cfUvid && cfUvid !== 'null') return `cfuvid_${cfUvid}`;

  return null;
}

function readFromGlobals(): string | null {
  const g = window as unknown as Record<string, unknown>;
  for (const key of ['cf_purchase', 'cf_order', '__CF_PURCHASE__', '__CF_ORDER__', 'cfData', 'ClickFunnels']) {
    const obj = g[key];
    if (obj && typeof obj === 'object') {
      const id = pickPurchaseId(obj as Record<string, unknown>);
      if (id) return id;
    }
  }
  for (const key of ['cf_purchase_id', 'purchase_id', 'cf_order_id']) {
    const v = g[key];
    if (typeof v === 'string' && v) return v;
    if (typeof v === 'number') return String(v);
  }
  return null;
}

function pickPurchaseId(obj: Record<string, unknown>): string | null {
  // Drill into a nested .purchase if present (mirrors webhook shape).
  const purchase = obj.purchase;
  if (purchase && typeof purchase === 'object') {
    const id = pickPurchaseId(purchase as Record<string, unknown>);
    if (id) return id;
  }
  for (const k of ['purchase_id', 'order_id', 'id']) {
    const v = obj[k];
    if (typeof v === 'string' && v) return v;
    if (typeof v === 'number') return String(v);
  }
  return null;
}

function readFromDom(): string | null {
  const el = document.querySelector<HTMLElement>(
    '[data-purchase-id], [data-cf-purchase-id], [data-order-id], [data-cf-order-id]'
  );
  return el?.dataset.purchaseId
      ?? el?.dataset.cfPurchaseId
      ?? el?.dataset.orderId
      ?? el?.dataset.cfOrderId
      ?? null;
}

function readValue(): number | undefined {
  const el = document.querySelector<HTMLElement>('[data-order-total], [data-cf-order-total], [data-purchase-total]');
  const raw = el?.dataset.orderTotal ?? el?.dataset.cfOrderTotal ?? el?.dataset.purchaseTotal;
  if (!raw) return undefined;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
}

function readCurrency(): string | undefined {
  const el = document.querySelector<HTMLElement>('[data-order-currency], [data-cf-order-currency]');
  return el?.dataset.orderCurrency ?? el?.dataset.cfOrderCurrency;
}
